import { ProtectedRoute } from '@hopper-b2b/hopper-auth';
import {
  PATH_410,
  PATH_ABOUT,
  PATH_AFFILIATES,
  PATH_CAREERS,
  PATH_CULTURE,
  PATH_DOWNLOAD,
  PATH_GIFT_CARD,
  PATH_HOME,
  PATH_JOBS,
  PATH_LEGAL,
  PATH_LOCALE,
  PATH_NOT_AVAILABLE_YET,
  PATH_REFER,
  PATH_RYANAIR,
  PRODUCT_PAGES,
  TRAVEL_PAGES,
} from '@marketing-site/utils/urlPaths';
import React, { PropsWithChildren, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom-v5-compat';
import {
  AboutPage,
  Careers,
  Culture,
  Download,
  Legal,
  LoadingPage,
  Product,
} from './pages';
import { PageGone } from './pages/410/410Page';
import { NotAvailableYet } from './pages/Download/NotAvailableYetPage';
import { GiftCardPage } from './pages/GiftCard/GiftCardPage';
import { ReferralPage } from './pages/Referral/ReferralPage';
import { EventActionName } from './types';
import { getLocaleFromPathname } from './utils/helpers';
import { useTrackMarketingSiteEvent } from './utils/hooks';
import { LocaleContext } from './utils/hooks/localeContext';
import { logPageView } from './utils/segmentAnalyticsHelpers';
import { ExternalRedirect } from './pages/Page/ExternalRedirect';
import { CanonicalHomeRedirect } from './pages/Page/CanonicalHomeRedirect';
import { useAuthProvider } from '@hopper-b2b/utilities';

declare global {
  interface Window {
    analytics: any;
  }
}

export function App() {
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname);

  const trackEvent = useTrackMarketingSiteEvent();

  useEffect(() => {
    trackEvent({ eventName: EventActionName.ViewedPage });
    // TODO - Replace with gtag(...)
    // https://hopper-jira.atlassian.net/browse/HDC-1322
    logPageView(locale);
  }, [location, locale, trackEvent]);

  return (
    <LocaleContext.Provider value={locale}>
      <ProtectedRouteWrapper>
        <Routes>
          <Route path={'/'} element={<CanonicalHomeRedirect />} />
          <Route
            path={PATH_LOCALE + PATH_HOME}
            element={<CanonicalHomeRedirect />}
          />

          <Route path={PATH_ABOUT} element={<AboutPage />} />
          <Route path={PATH_LOCALE + PATH_ABOUT} element={<AboutPage />} />

          <Route path={PATH_DOWNLOAD} element={<Download />} />
          <Route path={PATH_LOCALE + PATH_DOWNLOAD} element={<Download />} />

          <Route path={PATH_NOT_AVAILABLE_YET} element={<NotAvailableYet />} />
          <Route
            path={PATH_LOCALE + PATH_NOT_AVAILABLE_YET}
            element={<NotAvailableYet />}
          />

          <Route path={PATH_CAREERS} element={<Careers />} />
          <Route path={PATH_LOCALE + PATH_CAREERS} element={<Careers />} />

          <Route path={PATH_CULTURE} element={<Culture />} />
          <Route path={PATH_LOCALE + PATH_CULTURE} element={<Culture />} />

          <Route path={PRODUCT_PAGES} element={<Product />} />
          <Route path={PATH_LOCALE + PRODUCT_PAGES} element={<Product />} />

          <Route path={TRAVEL_PAGES} element={<Product />} />
          <Route path={PATH_LOCALE + TRAVEL_PAGES} element={<Product />} />

          <Route path={PATH_LEGAL} element={<Legal />} />
          <Route path={PATH_LOCALE + PATH_LEGAL} element={<Legal />} />

          <Route path={PATH_AFFILIATES} element={<PageGone />} />
          <Route path={PATH_LOCALE + PATH_AFFILIATES} element={<PageGone />} />

          <Route path={PATH_REFER} element={<ReferralPage />} />
          <Route path={PATH_LOCALE + PATH_REFER} element={<ReferralPage />} />

          <Route path={PATH_GIFT_CARD} element={<GiftCardPage />} />
          <Route
            path={PATH_LOCALE + PATH_GIFT_CARD}
            element={<GiftCardPage />}
          />

          <Route path={PATH_410} element={<PageGone />} />

          <Route path={'*'} element={<CanonicalHomeRedirect />} />

          <Route
            path={PATH_RYANAIR}
            element={<ExternalRedirect url="hopper-flights://closewebview" />}
          />
        </Routes>
      </ProtectedRouteWrapper>
    </LocaleContext.Provider>
  );
}

export default App;

const ProtectedRouteWrapper = ({ children }: PropsWithChildren) => {
  const { state } = useAuthProvider();
  return (
    <ProtectedRoute sessionInfo={state?.sessionInfo}>
      <Suspense fallback={<LoadingPage />}>{children}</Suspense>
    </ProtectedRoute>
  );
};
